import { noop } from 'lodash';
import { FolderColorMenuItems } from '@frameio/assets/folder';
import { useFolderHasActiveUploadLinksFragment } from '@frameio/assets/shared';
import { useFolderRestrictionsFragment } from '@frameio/assets/shared/gql/generated/FolderRestrictions';
import { select } from '@frameio/i18n';
import { useFolderPermissionSet } from '@frameio/permissions';
import { BetaChip } from '@frameio/shared/components/BetaChip';
import { NewChip } from '@frameio/shared/components/NewChip';
import { useIsMatchingClientName } from '@frameio/shared/contexts/ClientNameContext';
import { useIsLabsFeatureEnabled } from '@frameio/shared/hooks';
import {
  useMountErrorMessage,
  useOpenOnDesktopAction,
} from '@frameio/shared/resources/mounted-storage';
import { useCopyLink } from '@frameio/shared/routing';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { TrackingSourceType } from '@frameio/shared/segment/hooks/utils/trackingUtils';
import { useCreateShare } from '@frameio/shared/shares/hooks/useCreateShare';
import { UPLOAD_LINK_SOURCE } from '@frameio/shared/upload-links/constants';
import { AddToShareSubmenu } from '@frameio/shares/components';
import {
  CopyUploadLinkMenuItem,
  useCreateUploadLink,
  useIsUploadLinksEnabled,
} from '@frameio/upload-links';
import { MenuButton, MenuGroup, Text, theme, Tooltip } from '@frameio/vapor';
import {
  IconCloudArrow,
  IconDownload,
  IconLink,
  IconPeople,
  IconPersonCircleAdd,
  IconShare,
  IconTextCursor,
  IconTrash,
} from '@frameio/vapor-icons';
import { hasClipboardWritePermissions } from '@frameio/vapor-migration/hooks/useCopyText/useCopyText';
import {
  CreateFolderMenu,
  type CreateFolderMenuProps,
} from './CreateFolderMenu';
import TEXT from './generated/TEXT/FolderMenu';

export type FolderMenuProps = {
  folderId: string;
  isFolderDeleted?: boolean;
  isRootFolder: boolean;
  memberAccessBind?: any;
  onDeleteSelect: VoidFunction;
  onDownloadAllSelect: VoidFunction;
  onInviteSelect?: VoidFunction;
  onManageMembersSelect?: VoidFunction;
  onRenameSelect: VoidFunction;
  projectId: string;
  trackingSource?: TrackingSourceType;
} & Pick<CreateFolderMenuProps, 'onNewFolderSelect'>;

export function FolderMenu({
  folderId,
  isRootFolder,
  isFolderDeleted = false,
  memberAccessBind,
  onDeleteSelect,
  onDownloadAllSelect,
  onRenameSelect,
  onInviteSelect,
  onManageMembersSelect = noop,
  onNewFolderSelect = noop,
  projectId,
  trackingSource = 'BreadcrumbsFolderMenu',
}: FolderMenuProps) {
  const copyLink = useCopyLink();
  const isWebClient = useIsMatchingClientName('web');

  const { data: folderRestrictions } = useFolderRestrictionsFragment({
    from: {
      __typename: 'Asset',
      id: folderId,
    },
  });

  const permissions = useFolderPermissionSet({
    checks: [
      'canCreateChildren',
      'canDeleteChildren',
      'canDownloadChildren',
      'canEditUserPermissions',
      'canShareChildren',
      'canUpdateChildren',
    ],
    folderId,
  });

  const isColoredFolders = useIsLabsFeatureEnabled('folder_colors');
  const isUploadLinksEnabled = useIsUploadLinksEnabled();

  const canShare =
    !isFolderDeleted &&
    !isRootFolder &&
    !folderRestrictions.restricted &&
    !folderRestrictions.isRestrictedDescendant &&
    permissions.canShareChildren &&
    isWebClient;
  const canDelete =
    !isFolderDeleted && !isRootFolder && permissions.canDeleteChildren;
  const canDownload = !isFolderDeleted && permissions.canDownloadChildren;
  const canManageMembers =
    !isFolderDeleted && isRootFolder && permissions.canEditUserPermissions;
  const canRename =
    !isFolderDeleted && !isRootFolder && permissions.canUpdateChildren;

  const { createShareFromAssets } = useCreateShare();
  const track = useTrackUnsafe();

  const { createUploadLink } = useCreateUploadLink();
  const showUploadLink =
    isUploadLinksEnabled &&
    !isFolderDeleted &&
    isWebClient &&
    Boolean(permissions.canCreateChildren);

  const { data: uploadLinksData } =
    useFolderHasActiveUploadLinksFragment(folderId);
  const canCopyUploadLink =
    isUploadLinksEnabled &&
    !isFolderDeleted &&
    isWebClient &&
    Boolean(uploadLinksData?.hasActiveUploadLinks);

  const {
    icon: openOnDesktopIcon,
    isHidden: isOpenOnDesktopHidden,
    isDisabled: isOpenOnDesktopDisabled,
    label: openOnDesktopLabel,
    onPress: handleOpenOnDesktop,
    error: openOnDesktopError,
  } = useOpenOnDesktopAction({
    projectId,
    assetId: folderId,
    canOpenOnDesktop: canDownload,
    trackingSource,
  });
  const openOnDesktopErrorMessage = useMountErrorMessage(openOnDesktopError);

  const handleCopyLink = () => {
    copyLink(
      isFolderDeleted
        ? {
            page: 'trash',
            projectId,
            folderAssetId: isRootFolder ? undefined : folderId,
          }
        : {
            page: 'folder',
            projectId,
            folderAssetId: isRootFolder ? undefined : folderId,
          }
    );
  };

  const handleCreateShareLink = async () => {
    const assetIds = [folderId];
    const share = await createShareFromAssets(
      { assetIds, projectId },
      { shouldRedirect: false }
    );

    if (!share) {
      return;
    }

    track('static-share-created', {
      num_items_selected: assetIds.length,
      project_id: projectId,
      source: trackingSource,
      is_share_url_copied_automatically: Boolean(
        await hasClipboardWritePermissions()
      ),
      share_id: share.data?.createShareFromAssets?.share?.id,
    });
  };

  return (
    <>
      {(canShare || !isOpenOnDesktopHidden) && (
        <MenuGroup>
          {canShare && (
            <>
              <MenuButton
                iconBefore={<IconShare />}
                onPress={handleCreateShareLink}
              >
                {TEXT.createShareLink}
              </MenuButton>
              <AddToShareSubmenu assetIds={[folderId]} projectId={projectId} />
            </>
          )}

          {!isOpenOnDesktopHidden && (
            <Tooltip
              disabled={!isOpenOnDesktopDisabled}
              placement="left"
              title={openOnDesktopErrorMessage}
            >
              <span>
                <MenuButton
                  disabled={isOpenOnDesktopDisabled}
                  iconAfter={<NewChip />}
                  iconBefore={openOnDesktopIcon}
                  onPress={handleOpenOnDesktop}
                >
                  {openOnDesktopLabel}
                </MenuButton>
              </span>
            </Tooltip>
          )}
        </MenuGroup>
      )}

      {(showUploadLink ||
        canCopyUploadLink ||
        canManageMembers ||
        permissions.canCreateChildren) && (
        <MenuGroup>
          <CreateFolderMenu
            folderId={folderId}
            onNewFolderSelect={onNewFolderSelect}
            trackingSource={trackingSource}
          />

          {canManageMembers && (
            <>
              <MenuButton
                iconBefore={<IconPersonCircleAdd />}
                onPress={onInviteSelect}
                {...memberAccessBind}
              >
                {TEXT.inviteMembers}
              </MenuButton>
              <MenuButton
                iconBefore={<IconPeople />}
                onPress={onManageMembersSelect}
                {...memberAccessBind}
              >
                {TEXT.manageMembers}
              </MenuButton>
            </>
          )}

          {showUploadLink && (
            <MenuButton
              disabled={!permissions.canCreateChildren}
              iconAfter={<BetaChip />}
              iconBefore={<IconCloudArrow />}
              onPress={() =>
                createUploadLink(folderId, UPLOAD_LINK_SOURCE.FolderContextMenu)
              }
            >
              {TEXT.createUploadLink}
            </MenuButton>
          )}

          {canCopyUploadLink && <CopyUploadLinkMenuItem folderId={folderId} />}
        </MenuGroup>
      )}

      {isColoredFolders && (
        <MenuGroup>
          <FolderColorMenuItems folderIds={[folderId]} projectId={projectId} />
        </MenuGroup>
      )}

      {isWebClient && (
        <MenuGroup>
          <MenuButton iconBefore={<IconLink />} onPress={handleCopyLink}>
            {TEXT.copyLink(isRootFolder)}
          </MenuButton>
        </MenuGroup>
      )}

      {(canDownload || canRename || canDelete) && (
        <MenuGroup>
          {canDownload && (
            <MenuButton
              iconBefore={<IconDownload />}
              onPress={onDownloadAllSelect}
            >
              {TEXT.downloadAllFiles}
            </MenuButton>
          )}

          {canRename && (
            <MenuButton
              data-testid="header-panel-folder-menu-rename"
              iconBefore={<IconTextCursor />}
              onPress={onRenameSelect}
              shouldCloseMenuOnSelect={true}
            >
              {TEXT.rename}
            </MenuButton>
          )}

          {canDelete && (
            <MenuButton
              data-testid="header-panel-folder-menu-delete"
              iconBefore={
                <IconTrash color={theme.colors.icon.interactive.error} />
              }
              onPress={onDeleteSelect}
            >
              <Text color="error">{TEXT.delete}</Text>
            </MenuButton>
          )}
        </MenuGroup>
      )}
    </>
  );
}

TEXT.DEFINE(
  {
    /** Menu item label for copying the current folder or project link */
    copyLink: (isRootFolder: boolean) => {
      const value = String(isRootFolder);
      return select(value, {
        true: 'Copy Project URL',
        other: 'Copy Folder URL',
      });
    },
    /** Menu item label for creating a share link */
    createShareLink: 'Create Share Link',
    /** Menu item label for creating an upload link */
    createUploadLink: 'Create Upload Link',
    /** Menu item label for deleting the folder */
    delete: 'Delete',
    /** Menu item label for downloading all assets */
    downloadAllFiles: 'Download All Assets',
    /** Menu item label for inviting members */
    inviteMembers: 'Invite Members',
    /** Menu item label for managing members */
    manageMembers: 'Manage Members',
    /** Menu item label for renaming the folder */
    rename: 'Rename',
  },
  { String }
);
